import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  TextField,
  Autocomplete,
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useGetList, useDataProvider, useGetIdentity, useRefresh } from 'react-admin';
import { DEFAULTS } from '../../constants';

interface MedicalGroupRow {
  id: number;
  medical_group_number: string;
  medical_group_name: string;
  plan_location: string;
}

interface BulkAddMedicalGroupsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedClient: any;
}

const BulkAddMedicalGroupsDialog: React.FC<BulkAddMedicalGroupsDialogProps> = ({ isOpen, onClose, selectedClient }) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();

  const [medicalGroupRows, setMedicalGroupRows] = useState<MedicalGroupRow[]>([]);
  const [duplicateRows, setDuplicateRows] = useState<string[]>([]);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [hasPlanLocation, setHasPlanLocation] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);

  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');

  const isDataInvalid = medicalGroupRows.some(
    (row) => !row.medical_group_number.trim() || !row.medical_group_name.trim()
  );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setErrorMessage(null);
    setMedicalGroupRows([]);
    setHasPlanLocation(false);
    setDuplicateRows([]);

    if (file && selectedClient) {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const headers = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0] as string[];

          if (!headers.includes('medical_group_number') || !headers.includes('medical_group_name')) {
            setErrorMessage('Missing required columns: medical_group_number or medical_group_name');
            return;
          }

          const includesPlanLocation = headers.includes('plan_location');
          setHasPlanLocation(includesPlanLocation);

          const json = XLSX.utils.sheet_to_json(worksheet);

          const cleanedRows = json
            .filter((row: any) => row.medical_group_number && String(row.medical_group_number).trim() !== '')
            .map((row: any, index: number) => ({
              id: index,
              medical_group_number: String(row.medical_group_number || '').trim(),
              medical_group_name: String(row.medical_group_name || '').trim(),
              plan_location: includesPlanLocation ? String(row.plan_location || '').trim() : '',
            }));

          const columnActuallyHasData = cleanedRows.some((row) => row.plan_location !== '');
          setHasPlanLocation(includesPlanLocation && columnActuallyHasData);

          if (cleanedRows.length === 0) {
            setErrorMessage('The uploaded file appears to be empty.');
            return;
          }

          const seenNumbers = new Set();
          const internalDuplicates = new Set<string>();

          cleanedRows.forEach((row) => {
            if (seenNumbers.has(row.medical_group_number)) {
              internalDuplicates.add(row.medical_group_number);
            }
            seenNumbers.add(row.medical_group_number);
          });

          if (internalDuplicates.size > 0) {
            setErrorMessage(
              `Duplicate group numbers found in file: ${Array.from(internalDuplicates).join(
                ', '
              )}. Please fix the Excel and re-upload.`
            );
            return;
          }

          setMedicalGroupRows(cleanedRows);

          const numbersToCheck = cleanedRows.map((row) => row.medical_group_number);

          const { data: duplicates } = await dataProvider.getList('ValidateMedicalGroups', {
            pagination: { page: 1, perPage: 1000 },
            sort: { field: 'id', order: 'ASC' },
            filter: {
              billing_client_number: selectedClient.billing_client_number,
              medical_group_numbers: numbersToCheck,
            },
          });

          const duplicateNumbers = duplicates.map((item: any) => item.id);
          setDuplicateRows(duplicateNumbers);

          if (duplicateNumbers.length > 0) {
            setWarningMessage(`${duplicateNumbers.length} existing medical group(s) found. They will be updated.`);
          } else {
            setWarningMessage(null);
          }
        } catch (err) {
          setErrorMessage('Validation failed. Please check the file format or try again.');
        } finally {
          event.target.value = '';
        }
      };

      reader.readAsBinaryString(file);
    }
  };

  const handleRowChange = (id: number, field: keyof MedicalGroupRow, value: string) => {
    setMedicalGroupRows((prev) => {
      const newRows = prev.map((row) => (row.id === id ? { ...row, [field]: value } : row));

      const hasEmptyRequired = newRows.some((r) => !r.medical_group_number.trim() || !r.medical_group_name.trim());

      const numbers = newRows.map((r) => r.medical_group_number.trim()).filter(Boolean);
      const hasDuplicates = new Set(numbers).size !== numbers.length;

      if (hasEmptyRequired) {
        setErrorMessage('One or more required fields are empty. Please fill them in to save.');
      } else if (hasDuplicates) {
        setErrorMessage('Duplicate medical group numbers found in the grid. Each must be unique.');
      } else {
        setErrorMessage(null);
      }

      return newRows;
    });
  };

  const validateGridData = () => {
    setErrorMessage(null);

    for (let i = 0; i < medicalGroupRows.length; i++) {
      const row = medicalGroupRows[i];
      if (!row.medical_group_number || !row.medical_group_name) {
        const msg = `Row ${i + 1} is missing a required Number or Name.`;
        setErrorMessage(msg);
        toast.error(msg);
        return false;
      }
      return true;
    }

    const numbers = medicalGroupRows.map((r) => r.medical_group_number.trim());
    const unique = new Set(numbers);
    if (unique.size < numbers.length) {
      const msg = 'Duplicate medical group numbers found in the grid.';
      setErrorMessage(msg);
      toast.error(msg);
      return false;
    }

    return true;
  };

  const revalidateWithBackend = async (rows: MedicalGroupRow[]) => {
    const numbersToCheck = rows.map((row) => row.medical_group_number.trim()).filter(Boolean);

    if (numbersToCheck.length === 0) return;

    try {
      const { data: duplicates } = await dataProvider.getList('ValidateMedicalGroups', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {
          billing_client_number: selectedClient.billing_client_number,
          medical_group_numbers: numbersToCheck,
        },
      });

      const duplicateNumbers = duplicates.map((item: any) => item.id);
      setDuplicateRows(duplicateNumbers);

      if (duplicateNumbers.length > 0) {
        setWarningMessage(`${duplicateNumbers.length} existing medical group(s) found. They will be updated.`);
      } else {
        setWarningMessage(null);
      }
    } catch (err) {}
  };

  const executeSave = async () => {
    setConfirmOpen(false);

    if (!selectedClient || medicalGroupRows.length === 0) {
      toast.info('No medical groups to save.');
      return;
    }
    const payload = {
      import_mode: importMode,
      billing_client_number: selectedClient.billing_client_number,
      billing_client_name: selectedClient.billing_client_name,
      medical_groups: medicalGroupRows.map((row) => ({
        medical_group_number: row.medical_group_number,
        medical_group_name: row.medical_group_name,
        plan_location: row.plan_location,
        created_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
        last_modified_by_text: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      })),
    };

    try {
      const { data: result } = await dataProvider.create('BulkAddMedicalGroups', { data: payload });
      toast.success(result.message || 'Medical groups saved successfully!');
      refresh();
      onClose();
    } catch (error: any) {
      toast.error(`Save failed: ${error.message || 'Unknown error'}`);
    }
  };

  const handleSaveClick = () => {
    if (!validateGridData()) return;
    if (duplicateRows.length > 0) {
      setConfirmOpen(true);
    } else {
      executeSave();
    }
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Bulk Add Medical Groups</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} alignItems="center" mb={3}>
            <Box display="flex" gap={2} alignItems="center" mb={3}>
              <TextField label="Client Name" value={selectedClient?.billing_client_name || ''} fullWidth disabled />
              <TextField
                label="Client Number"
                value={selectedClient?.billing_client_number || ''}
                disabled
                sx={{ width: '200px' }}
              />
            </Box>
          </Box>

          <>
            <Typography variant="h6" gutterBottom>
              Upload Excel
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Expected columns: `medical_group_number`, `medical_group_name`, `plan_location`
            </Typography>
            <Button variant="outlined" startIcon={<UploadFileIcon />} component="label" sx={{ mt: 1 }}>
              Upload File
              <input type="file" accept=".xlsx" hidden onChange={handleFileChange} />
            </Button>
          </>

          {medicalGroupRows.length > 0 && (
            <Box
              mt={3}
              mb={2}
              p={2.5}
              sx={{
                backgroundColor: 'transparent',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                Import Method
              </Typography>
              <Box display="flex" gap={6}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="importMode"
                    value="append"
                    checked={importMode === 'append'}
                    onChange={() => setImportMode('append')}
                    style={{ marginRight: '10px', accentColor: '#00838f' }}
                  />
                  <Typography variant="body2">
                    <strong>Append / Update:</strong> Adds new records and updates existing ones highlighted in red.
                  </Typography>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="importMode"
                    value="replace"
                    checked={importMode === 'replace'}
                    onChange={() => setImportMode('replace')}
                    style={{ marginRight: '10px', accentColor: '#00838f' }}
                  />
                  <Typography variant="body2">
                    <strong>Replace All:</strong> Removes all existing records for this client and replaces them with
                    the data below.
                  </Typography>
                </label>
              </Box>
            </Box>
          )}

          {medicalGroupRows.length > 0 && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Review and edit Medical Groups
              </Typography>
              <Paper>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Medical Group Number</TableCell>
                      <TableCell>Medical Group Name</TableCell>
                      {hasPlanLocation && <TableCell>Plan Location</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {medicalGroupRows.map((row) => {
                      const isDuplicate = duplicateRows.includes(row.medical_group_number);
                      const shouldHighlight = importMode === 'append' && isDuplicate;
                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            backgroundColor: shouldHighlight ? 'rgba(255, 204, 204, 0.5)' : 'transparent',
                          }}
                        >
                          <TableCell>
                            <TextField
                              variant="standard"
                              fullWidth
                              value={row.medical_group_number}
                              onChange={(e) => handleRowChange(row.id, 'medical_group_number', e.target.value)}
                              onBlur={() => revalidateWithBackend(medicalGroupRows)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              fullWidth
                              value={row.medical_group_name}
                              onChange={(e) => handleRowChange(row.id, 'medical_group_name', e.target.value)}
                            />
                          </TableCell>
                          {hasPlanLocation && (
                            <TableCell>
                              <TextField
                                variant="standard"
                                fullWidth
                                value={row.plan_location}
                                onChange={(e) => handleRowChange(row.id, 'plan_location', e.target.value)}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            </Box>
          )}
          {errorMessage && (
            <Box
              mt={2}
              p={1.5}
              sx={{
                backgroundColor: '#fff5f5',
                border: '1px solid #ffcccc',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography color="error" variant="body2" sx={{ fontWeight: 500 }}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {warningMessage && !errorMessage && importMode === 'append' && (
            <Box
              mt={2}
              p={1.5}
              sx={{
                backgroundColor: '#fffbeb',
                border: '1px solid #fcd34d',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography sx={{ color: '#92400e', fontSize: '0.875rem', fontWeight: 500 }}>
                ⚠️ {warningMessage}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSaveClick}
            variant="contained"
            disabled={medicalGroupRows.length === 0 || isDataInvalid || !!errorMessage}
          >
            Save Medical Groups
          </Button>
        </DialogActions>
      </Dialog>

      {isConfirmOpen && (
        <Dialog open={isConfirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle sx={{ color: importMode === 'replace' ? 'error.main' : 'inherit' }}>
            {importMode === 'replace' ? '⚠️ Confirm Replace All' : 'Confirm Save'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {importMode === 'replace'
                ? `Warning: This will DELETE ALL existing medical groups for ${selectedClient?.billing_client_name} and replace them with these ${medicalGroupRows.length} records. This action cannot be undone.`
                : `This will add ${medicalGroupRows.length} records. ${duplicateRows.length} existing records (highlighted in red) will be updated with the new data. Proceed?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={executeSave}
              variant="contained"
              color={importMode === 'replace' ? 'error' : 'primary'}
              autoFocus
            >
              Confirm & Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default BulkAddMedicalGroupsDialog;
